import { withErrorCode } from '@/lib/errors'
import type { UseFormRegister, FieldErrors } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { MagicInput as Input } from '@/components/ui/magic-input'
import { Label } from '@/components/ui/label'
import type { IdentityFormValues } from '../types'

const DISCOUNT_OPTIONS = [
  { value: 'none' as const,       label: 'None' },
  { value: 'student' as const,    label: 'Student — 10% off (show student ID)' },
  { value: 'nonprofit' as const,  label: '501(c)(3) or fiscally sponsored — 10% off' },
  { value: 'odd_artist' as const, label: 'ODD Artist — 20% off' },
]

interface IdentitySectionProps {
  register: UseFormRegister<IdentityFormValues>
  errors: FieldErrors<IdentityFormValues>
  discountValue: string
  onDiscountChange: (value: 'none' | 'student' | 'nonprofit' | 'odd_artist') => void
  isLoggedIn?: boolean
}

export function IdentitySection({
  register,
  errors,
  discountValue,
  onDiscountChange,
  isLoggedIn = false,
}: IdentitySectionProps) {
  return (
    <section className="space-y-6">
      {!isLoggedIn && (
        <div className="rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
          <Link to="/sign-in" className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
            Sign in
          </Link>{' '}
          to have your contact info filled in automatically.
        </div>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Left: Contact info */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold">Your Info</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                placeholder="Jane"
                aria-invalid={!!errors.firstName}
                {...register('firstName')}
              />
              {errors.firstName && (
                <p className="text-xs text-destructive">{withErrorCode(String(errors.firstName.message), 'app/validation')}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                placeholder="Doe"
                aria-invalid={!!errors.lastName}
                {...register('lastName')}
              />
              {errors.lastName && (
                <p className="text-xs text-destructive">{withErrorCode(String(errors.lastName.message), 'app/validation')}</p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              aria-invalid={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{withErrorCode(String(errors.email.message), 'app/validation')}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="organizationName">Organization Name (optional)</Label>
            <Input
              id="organizationName"
              placeholder="Organization (if applicable)"
              {...register('organizationName')}
            />
          </div>
        </div>

        {/* Right: Discount */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold">Discount Qualification</h2>
          <p className="text-sm text-muted-foreground">
            Do you qualify for one of these discounts? (select one)
          </p>
          <div className="space-y-2">
            {DISCOUNT_OPTIONS.map(({ value, label }) => (
              <label key={value} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="discountCategory"
                  value={value}
                  checked={discountValue === value}
                  onChange={() => onDiscountChange(value)}
                  className="accent-primary"
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
